"use client";

import React from "react";
import { Facebook, Instagram, Linkedin, Twitter, MapPin, Phone, Mail, Send } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function NewsletterForm() {
  const {
    handleSubmit,
    control,
    isSubmitting,
    isSubmitted,
    successMessage
  } = useWvcForm();
  if (isSubmitted) {
    return <div data-section-id="4802" className="p-4 bg-primary/20 border border-primary/30 rounded-lg text-primary-foreground">
        <p className="text-sm font-medium">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-2">
      <div className="flex gap-2">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email"
        }
      }} render={({
        field
      }) => <FormItem className="flex-1 space-y-0">
              <FormControl>
                <Input placeholder="Enter your email" type="email" className="bg-secondary/50 border-white/20 text-foreground placeholder:text-muted-foreground focus-visible:ring-primary/50 h-10 rounded-md" {...field} />
              </FormControl>
              <FormMessage className="text-xs mt-1 text-primary" />
            </FormItem>} />
        <Button type="submit" size="icon" disabled={isSubmitting} className="h-10 w-10 shrink-0 bg-primary text-primary-foreground hover:bg-primary/90 shadow-md">
          {isSubmitting ? <span className="animate-spin">⟳</span> : <Send className="size-4" />}
          <span className="sr-only">Subscribe</span>
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Subscribe for emergency auto security updates and offers.
      </p>
    </Form>;
}
function FooterMenuLinks() {
  const {
    menuItems,
    loading,
    error
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-3">
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-4 w-24 bg-white/10 animate-pulse rounded" />)}
      </div>;
  }
  if (error || !menuItems || menuItems.length === 0) {
    const fallbackLinks = [{
      label: "Home",
      href: "/HomePage"
    }, {
      label: "Lost Keys",
      href: "/LostKeysPage"
    }, {
      label: "Areas Covered",
      href: "/AreasCoveredPage"
    }, {
      label: "Contact Us",
      href: "#"
    }];
    return <ul className="space-y-3">
        {fallbackLinks.map((item, i) => <li key={i} data-index={i}>
            <Link to={item.href} className="text-sm font-medium hover:text-primary transition-colors duration-200 block tracking-tight">
              {item.label}
            </Link>
          </li>)}
      </ul>;
  }
  return <ul className="space-y-3">
      {menuItems.map((item, i) => <li key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="text-sm font-medium hover:text-primary transition-colors duration-200 block tracking-tight">
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
export default function Footer() {
  const currentYear = new Date().getFullYear();
  return <section id="footer" className="dark bg-secondary text-muted-foreground border-t border-white/10">
      <div className="container mx-auto px-4 py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {}
          <div className="space-y-6">
            <Link to="/HomePage" className="block">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1573441/img/tarus_x_logo_white.png" alt="AngliaKeys Auto Locksmith" className="h-8 w-auto opacity-100 hover:opacity-90 transition-opacity" />
            </Link>
            <div className="space-y-4">
              <p className="text-sm leading-relaxed max-w-xs font-sans">
                Professional car auto locksmith serving Norfolk and East Anglia. Rapid response for lost keys and lockouts, 24/7.
              </p>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-xs font-bold text-primary uppercase tracking-wider">
                <span>24/7 Mobile Service</span>
              </div>
            </div>
          </div>

          {}
          <div className="space-y-6">
            <h3 className="text-foreground font-bold tracking-tighter uppercase text-sm">Quick Links</h3>
            <MenuProvider menu_id="17" className="w-full">
              <FooterMenuLinks />
            </MenuProvider>
          </div>

          {}
          <div className="space-y-6">
            <h3 className="text-foreground font-bold tracking-tighter uppercase text-sm">Norfolk Service Areas</h3>
            <ul className="space-y-4 font-mono">
              {[{
              city: "Norwich, Norfolk",
              icon: MapPin
            }, {
              city: "King's Lynn, Norfolk",
              icon: MapPin
            }, {
              city: "Great Yarmouth, Norfolk",
              icon: MapPin
            }].map((area, i) => <li key={i} data-index={i} className="flex items-start gap-3 text-xs group">
                  <area.icon className="size-4 text-primary mt-0.5 shrink-0 group-hover:scale-110 transition-transform" />
                  <span className="group-hover:text-foreground transition-colors">{area.city}</span>
                </li>)}
            </ul>
            <div className="pt-2">
              <Link to="/AreasCoveredPage" className="text-xs font-bold text-primary hover:text-primary/80 flex items-center gap-1 uppercase tracking-widest">
                Full Coverage Map <span aria-hidden="true">&rarr;</span>
              </Link>
            </div>
          </div>

          {}
          <div className="space-y-6">
            <h3 className="text-foreground font-bold tracking-tighter uppercase text-sm">Stay Connected</h3>
            
            <div className="flex gap-4">
              {[{
              icon: Facebook,
              href: "#",
              label: "Facebook"
            }, {
              icon: Twitter,
              href: "#",
              label: "Twitter"
            }, {
              icon: Instagram,
              href: "#",
              label: "Instagram"
            }, {
              icon: Linkedin,
              href: "#",
              label: "LinkedIn"
            }].map((social, i) => <Link key={i} data-index={i} to={social.href} className="bg-white/5 p-2.5 rounded-lg hover:bg-primary hover:text-primary-foreground transition-all duration-300 text-muted-foreground shadow-sm" aria-label={social.label}>
                  <social.icon className="size-4" />
                </Link>)}
            </div>

            <div className="space-y-3">
              <h4 className="text-xs font-bold text-foreground uppercase tracking-widest">Newsletter</h4>
              <FormProvider formId="footer-newsletter" sectionName="footer" defaultValues={{
              email: ""
            }} successMessage="You're subscribed to emergency alerts!">
                <NewsletterForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="border-t border-white/5 bg-black/20 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6 text-[11px] text-muted-foreground/60 font-mono uppercase tracking-widest">
            <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-8">
              <span>&copy; {currentYear} AngliaKeys Auto Locksmith. All rights reserved.</span>
              <span className="hidden sm:inline text-white/10">/</span>
              <Link to="#" className="hover:text-primary transition-colors">Privacy Policy</Link>
              <Link to="#" className="hover:text-primary transition-colors">Terms of Service</Link>
            </div>
            <div className="flex items-center gap-2 px-3 py-1 rounded-md bg-white/5 border border-white/10">
              <span className="size-1.5 rounded-full bg-primary animate-pulse" />
              <span className="font-bold text-foreground/80">Mechanical Precision / Digital Urgency</span>
            </div>
          </div>
        </div>
      </div>
    </section>;
}